/* eslint-disable complexity */
import { useCallback, useReducer } from 'react';
import { EMPTY_STRING } from '@/data/constants/marketing';
import {
	DP_FAN_STAGES,
	DpFanStagesStateSports,
	DpFanStagesStateSportGroups,
	DpFanStagesStateTeams,
	DpFanStagesStateBrands,
	DpFanStagesStateSizes,
} from '@/data/Content/DpFan/types';

export type DpFanStagesState = {
	stage: DP_FAN_STAGES;
	stepIndex: number;
	activeCategory: string;
	activeGroup: string;
	sports: DpFanStagesStateSports;
	teams: DpFanStagesStateTeams;
	brands: DpFanStagesStateBrands;
	size: DpFanStagesStateSizes;
	submitting: boolean;
	needToSync: boolean;
	sportGroups: DpFanStagesStateSportGroups;
	selectedGroups: DpFanStagesStateSportGroups;
};

export const initialDpFanStages: DpFanStagesState = {
	stage: DP_FAN_STAGES.INITITAL,
	stepIndex: 0,
	activeCategory: '',
	activeGroup: '',
	sports: [],
	teams: {},
	brands: [],
	size: {},
	submitting: false,
	needToSync: true,
	sportGroups: {},
	selectedGroups: {},
};

export enum DP_FAN_STAGES_ACTIONS {
	START_SUBMIT = 'START_SUBMIT',
	CHANGE_SPORT = 'CHANGE_SPORT',
	CHANGE_LEAGUE = 'CHANGE_LEAGUE',
	CHANGE_TEAM = 'CHANGE_TEAM',
	CHANGE_BRAND = 'CHANGE_BRAND',
	CHANGE_SIZE = 'CHANGE_SIZE',
	END_SUBMIT = 'END_SUBMIT',
	PREV_STAGE = 'PREV_STAGE',
	NEXT_STAGE = 'NEXT_STAGE',
	PARTIAL_UPDATE = 'PARTIAL_UPDATE',
}

type DpFanStagesAction =
	| { type: DP_FAN_STAGES_ACTIONS.START_SUBMIT }
	| { type: DP_FAN_STAGES_ACTIONS.END_SUBMIT }
	| { type: DP_FAN_STAGES_ACTIONS.CHANGE_SPORT; sport: string }
	| {
			type: DP_FAN_STAGES_ACTIONS.CHANGE_LEAGUE;
			category: string;
			league: string;
			leagueTeamNames: string[];
	  }
	| { type: DP_FAN_STAGES_ACTIONS.CHANGE_TEAM; category: string; team: string }
	| { type: DP_FAN_STAGES_ACTIONS.CHANGE_BRAND; brand: string }
	| { type: DP_FAN_STAGES_ACTIONS.CHANGE_SIZE; category: string; size: string }
	| {
			type: DP_FAN_STAGES_ACTIONS.PREV_STAGE;
			updates?: Partial<DpFanStagesState>;
	  }
	| {
			type: DP_FAN_STAGES_ACTIONS.NEXT_STAGE;
			updates?: Partial<DpFanStagesState>;
	  }
	| { type: DP_FAN_STAGES_ACTIONS.PARTIAL_UPDATE; updates: Partial<DpFanStagesState> };

const getSteps = (
	sports: string[],
	sportGroups: DpFanStagesStateSportGroups = {},
	selectedGroups: DpFanStagesStateSportGroups = {}
) => {
	const steps: { stage: DP_FAN_STAGES; category: string; group: string }[] = [
		{ stage: DP_FAN_STAGES.INITITAL, category: '', group: '' },
		{ stage: DP_FAN_STAGES.SPORTS, category: '', group: '' },
	];

	const hasLeagues = (sport: string) =>
		(sportGroups[sport] ?? []).some((group) => group !== sport);
	const orderedSports = [
		...sports.filter((sport) => hasLeagues(sport)),
		...sports.filter((sport) => !hasLeagues(sport)),
	];

	orderedSports.forEach((sport) => {
		const groups = sportGroups[sport] ?? [];

		if (hasLeagues(sport)) {
			steps.push({ stage: DP_FAN_STAGES.LEAGUES, category: sport, group: '' });
			(selectedGroups[sport] ?? []).forEach((group) => {
				steps.push({ stage: DP_FAN_STAGES.TEAMS, category: sport, group });
			});
			return;
		}

		groups.forEach((group) => {
			steps.push({ stage: DP_FAN_STAGES.TEAMS, category: sport, group });
		});
	});

	steps.push({ stage: DP_FAN_STAGES.BRANDS, category: '', group: '' });
	steps.push({ stage: DP_FAN_STAGES.SIZES, category: '', group: '' });
	steps.push({ stage: DP_FAN_STAGES.FINISH, category: '', group: '' });

	return steps;
};

export const dpFanStagesReducer = (
	state: DpFanStagesState,
	action: DpFanStagesAction
): DpFanStagesState => {
	switch (action.type) {
		case DP_FAN_STAGES_ACTIONS.START_SUBMIT:
			return { ...state, submitting: true };
		case DP_FAN_STAGES_ACTIONS.END_SUBMIT:
			return { ...state, submitting: false };
		case DP_FAN_STAGES_ACTIONS.PREV_STAGE: {
			const steps = getSteps(state.sports, state.sportGroups, state.selectedGroups);
			const newIndex = Math.max(0, state.stepIndex - 1);
			const prevStep = steps[newIndex];
			return {
				...state,
				stepIndex: newIndex,
				stage: prevStep.stage,
				activeCategory: prevStep.category,
				activeGroup: prevStep.group,
				...action.updates,
			};
		}
		case DP_FAN_STAGES_ACTIONS.NEXT_STAGE: {
			const steps = getSteps(state.sports, state.sportGroups, state.selectedGroups);
			const newIndex = Math.min(steps.length - 1, state.stepIndex + 1);
			const nextStep = steps[newIndex];
			return {
				...state,
				stepIndex: newIndex,
				stage: nextStep.stage,
				activeCategory: nextStep.category,
				activeGroup: nextStep.group,
				...action.updates,
			};
		}
		case DP_FAN_STAGES_ACTIONS.CHANGE_SPORT: {
			const isExist = state.sports.includes(action.sport);
			const newSports = isExist
				? state.sports.filter((sport) => sport !== action.sport)
				: [...state.sports, action.sport];

			const newTeams = { ...state.teams };
			delete newTeams[action.sport];

			const newSelectedGroups = { ...state.selectedGroups };
			delete newSelectedGroups[action.sport];

			return { ...state, sports: newSports, teams: newTeams, selectedGroups: newSelectedGroups };
		}
		case DP_FAN_STAGES_ACTIONS.CHANGE_LEAGUE: {
			const current = state.selectedGroups[action.category] ?? [];
			const isExist = current.includes(action.league);
			const newLeagues = isExist
				? current.filter((league) => league !== action.league)
				: [...current, action.league];

			const newSelectedGroups = { ...state.selectedGroups };
			if (newLeagues.length === 0) {
				delete newSelectedGroups[action.category];
			} else {
				newSelectedGroups[action.category] = newLeagues;
			}

			const newSports = [...state.sports];
			if (!isExist && !newSports.includes(action.category)) {
				newSports.push(action.category);
			}

			const newTeams = { ...state.teams };
			if (isExist && action.leagueTeamNames.length > 0) {
				const remaining = (newTeams[action.category] ?? []).filter(
					(team) => !action.leagueTeamNames.includes(team)
				);
				if (remaining.length > 0) {
					newTeams[action.category] = remaining;
				} else {
					delete newTeams[action.category];
				}
			}

			return {
				...state,
				selectedGroups: newSelectedGroups,
				sports: newSports,
				teams: newTeams,
			};
		}
		case DP_FAN_STAGES_ACTIONS.CHANGE_BRAND: {
			const isExist = state.brands.includes(action.brand);
			const newBrands = isExist
				? state.brands.filter((brand) => brand !== action.brand)
				: [...state.brands, action.brand];

			return { ...state, brands: newBrands };
		}
		case DP_FAN_STAGES_ACTIONS.CHANGE_TEAM: {
			const data = state.teams[action.category] || [];
			const isExist = data.includes(action.team);
			const newTeams = isExist
				? data.filter((team) => team !== action.team)
				: [...data, action.team];

			let newSports = [...state.sports];
			if (!newSports.includes(action.category)) {
				newSports.push(action.category);
			}

			const newTeamState = { ...state.teams, [action.category]: newTeams };

			if (newTeams.length === 0) {
				delete newTeamState[action.category];
				newSports = newSports.filter((sport) => sport !== action.category);
			}

			return {
				...state,
				teams: { ...newTeamState },
				sports: newSports,
			};
		}
		case DP_FAN_STAGES_ACTIONS.CHANGE_SIZE: {
			const isDeleted = action.size === EMPTY_STRING;
			const newSizes = { ...state.size };

			if (isDeleted) {
				delete newSizes[action.category];
			} else {
				newSizes[action.category] = action.size;
			}

			return { ...state, size: newSizes };
		}
		case DP_FAN_STAGES_ACTIONS.PARTIAL_UPDATE:
			return { ...state, ...action.updates };
		default:
			return state;
	}
};

export const useDpFanState = () => {
	const [dpFanState, dispatch] = useReducer(dpFanStagesReducer, initialDpFanStages);

	const onChangeSportState = useCallback((sport: string) => {
		dispatch({ type: DP_FAN_STAGES_ACTIONS.CHANGE_SPORT, sport });
	}, []);

	const onChangeTeamState = useCallback((category: string, team: string) => {
		dispatch({ type: DP_FAN_STAGES_ACTIONS.CHANGE_TEAM, category, team });
	}, []);

	const onChangeLeagueState = useCallback(
		(category: string, league: string, leagueTeamNames: string[]) => {
			dispatch({ type: DP_FAN_STAGES_ACTIONS.CHANGE_LEAGUE, category, league, leagueTeamNames });
		},
		[]
	);

	const onChangeSizeState = useCallback((category: string, value: string) => {
		dispatch({ type: DP_FAN_STAGES_ACTIONS.CHANGE_SIZE, category, size: value });
	}, []);

	const onPrevStageState = useCallback(() => {
		dispatch({ type: DP_FAN_STAGES_ACTIONS.PREV_STAGE });
	}, []);

	const onNextStageState = useCallback(() => {
		dispatch({ type: DP_FAN_STAGES_ACTIONS.NEXT_STAGE });
	}, []);

	const onChangeBrandState = useCallback((brand: string) => {
		dispatch({ type: DP_FAN_STAGES_ACTIONS.CHANGE_BRAND, brand });
	}, []);

	const onChangeActiveSportState = useCallback((sport: string) => {
		dispatch({ type: DP_FAN_STAGES_ACTIONS.PARTIAL_UPDATE, updates: { activeCategory: sport } });
	}, []);

	const onStartSubmitState = useCallback(() => {
		dispatch({ type: DP_FAN_STAGES_ACTIONS.START_SUBMIT });
	}, []);

	const onEndSubmitState = useCallback(() => {
		dispatch({ type: DP_FAN_STAGES_ACTIONS.END_SUBMIT });
	}, []);

	const onPartialUpdateState = useCallback((updates: Partial<DpFanStagesState>) => {
		dispatch({ type: DP_FAN_STAGES_ACTIONS.PARTIAL_UPDATE, updates });
	}, []);

	return {
		dpFanState,
		onChangeSizeState,
		onChangeSportState,
		onChangeBrandState,
		onChangeTeamState,
		onChangeLeagueState,
		onChangeActiveSportState,
		onPrevStageState,
		onNextStageState,
		onStartSubmitState,
		onEndSubmitState,
		onPartialUpdateState,
	};
};
